import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Edit2, Plus, UserPlus, X } from "lucide-react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

import { useAuthContext } from "@/Components/AuthContext";
import BreadCrumb from "@/Components/Common/BreadCrumb";
import { Badge, Button, Card, CardBody, CardHeader, Col, Input, Label, Row } from "@/Components/ui";
import { SITE_URL } from "@/api";
import { groupReservationService } from "@/api/services/group-reservation.service";
import {
  GroupReservationAgencyLink,
  GroupReservationAgencyLinkPayload,
  GroupReservationAdminMarketer,
} from "@/api/types/group-reservation";
import { SUBDOMAIN } from "@/assets/meta/meta";
import GroupReservationPageShell from "../GroupReservationPageShell";

const emptyForm = (): GroupReservationAgencyLinkPayload => ({
  agency_user: 0,
  label: "",
  commission_rate: "0.00",
  is_active: true,
  memo: "",
});

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("ko-KR", { hour12: false });
};

const marketerLabel = (marketer?: GroupReservationAdminMarketer | null) => {
  if (!marketer) return "-";
  return marketer.display_name || marketer.username || marketer.phone_num || `마케터 #${marketer.user}`;
};

const buildAgencyUrl = (token: string) => `${SITE_URL}/group-reservation/agency/${token}`;

export default function GroupReservationAgencyLinksPage() {
  const navigate = useNavigate();
  const { userInfo } = useAuthContext();
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<GroupReservationAgencyLinkPayload>(emptyForm());
  const canSetCommissionRate = Boolean(
    userInfo?.is_global_admin ||
      (Number(userInfo?.site_permission?.permission) === 2 && userInfo?.site_permission?.subdomain === SUBDOMAIN)
  );

  const marketersQuery = useQuery({
    queryKey: ["group-reservation", "admin-marketers"],
    queryFn: () => groupReservationService.getAdminMarketers(),
  });

  const linksQuery = useQuery({
    queryKey: ["group-reservation", "agency-links"],
    queryFn: () => groupReservationService.getAgencyLinks(),
  });

  const marketers = marketersQuery.data?.data.results ?? [];
  const links = linksQuery.data?.data.results ?? [];
  const marketerByUser = useMemo(
    () => new Map(marketers.map((marketer) => [marketer.user, marketer])),
    [marketers]
  );

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm());
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        ...form,
        label: form.label.trim(),
        commission_rate: String(form.commission_rate || "0"),
        memo: form.memo?.trim() || null,
      };
      if (editingId) {
        return groupReservationService.updateAgencyLink(editingId, payload);
      }
      return groupReservationService.createAgencyLink(payload);
    },
    onSuccess: () => {
      toast.success(editingId ? "대리점 링크를 수정했습니다." : "대리점 링크를 발급했습니다.");
      resetForm();
      queryClient.invalidateQueries({ queryKey: ["group-reservation", "agency-links"] });
      queryClient.invalidateQueries({ queryKey: ["group-reservation", "admin-marketers"] });
    },
    onError: (error: any) => toast.error(error?.message || "대리점 링크 저장에 실패했습니다."),
  });

  const startEdit = (link: GroupReservationAgencyLink) => {
    setEditingId(link.id);
    setForm({
      agency_user: link.agency_user,
      label: link.label,
      commission_rate: link.commission_rate || "0.00",
      is_active: link.is_active,
      memo: link.memo || "",
    });
  };

  const submit = () => {
    if (!form.agency_user) {
      toast.error("마케터를 선택해주세요.");
      return;
    }
    if (!form.label.trim()) {
      toast.error("링크명을 입력해주세요.");
      return;
    }
    const rate = Number(form.commission_rate);
    if (!Number.isFinite(rate) || rate < 0 || rate > 100) {
      toast.error("수수료율은 0 이상 100 이하로 입력해주세요.");
      return;
    }
    saveMutation.mutate();
  };

  const selectMarketer = (userId: number) => {
    const marketer = marketerByUser.get(userId);
    setForm((prev) => ({
      ...prev,
      agency_user: userId,
      label: prev.label || marketerLabel(marketer),
      commission_rate: canSetCommissionRate ? marketer?.commission_rate || prev.commission_rate || "0.00" : prev.commission_rate,
    }));
  };

  const copyUrl = async (token: string) => {
    const url = buildAgencyUrl(token);
    if (!navigator?.clipboard?.writeText) {
      toast.error("브라우저에서 링크 복사를 지원하지 않습니다.");
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("대리점 신청 링크를 복사했습니다.");
    } catch {
      toast.error("대리점 신청 링크 복사에 실패했습니다.");
    }
  };

  return (
    <GroupReservationPageShell>
      <BreadCrumb title="단체예약 대리점 링크" pageTitle="예약" />

      <Row className="g-3">
        <Col xl={4}>
          <Card>
            <CardHeader>{editingId ? "대리점 링크 수정" : "대리점 링크 발급"}</CardHeader>
            <CardBody>
              <div className="mb-3">
                <Label htmlFor="group-agency-marketer">마케터</Label>
                <Input
                  id="group-agency-marketer"
                  type="select"
                  value={form.agency_user || ""}
                  onChange={(event) => selectMarketer(Number(event.target.value))}
                  disabled={Boolean(editingId)}
                >
                  <option value="">마케터 선택</option>
                  {marketers.map((marketer) => (
                    <option key={marketer.user} value={marketer.user}>
                      {marketerLabel(marketer)} ({marketer.phone_num || "-"})
                    </option>
                  ))}
                </Input>
              </div>
              <div className="mb-3">
                <Label htmlFor="group-agency-label">링크명</Label>
                <Input
                  id="group-agency-label"
                  value={form.label}
                  onChange={(event) => setForm((prev) => ({ ...prev, label: event.target.value }))}
                />
              </div>
              <Row className="g-3">
                <Col md={6}>
                  <Label htmlFor="group-agency-commission">수수료율(%)</Label>
                  <Input
                    id="group-agency-commission"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={form.commission_rate}
                    disabled={!canSetCommissionRate}
                    onChange={(event) => setForm((prev) => ({ ...prev, commission_rate: event.target.value }))}
                  />
                </Col>
                <Col md={6}>
                  <Label className="d-block">상태</Label>
                  <label className="d-flex align-items-center gap-2 mt-2">
                    <Input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(event) => setForm((prev) => ({ ...prev, is_active: event.target.checked }))}
                    />
                    <span>신청 링크 활성화</span>
                  </label>
                </Col>
              </Row>
              <div className="mt-3">
                <Label htmlFor="group-agency-memo">메모</Label>
                <Input
                  id="group-agency-memo"
                  type="textarea"
                  rows={3}
                  value={form.memo || ""}
                  onChange={(event) => setForm((prev) => ({ ...prev, memo: event.target.value }))}
                />
              </div>
              <div className="mt-4 d-flex gap-2">
                <Button onClick={submit} disabled={saveMutation.isPending || marketers.length === 0}>
                  <Plus size={16} className="me-1" />
                  {editingId ? "수정" : "발급"}
                </Button>
                {editingId ? (
                  <Button color="soft-secondary" onClick={resetForm} disabled={saveMutation.isPending}>
                    <X size={16} className="me-1" />
                    취소
                  </Button>
                ) : null}
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col xl={8}>
          <Card>
            <CardHeader>대리점 링크 목록</CardHeader>
            <CardBody>
              {marketersQuery.isLoading || linksQuery.isLoading ? (
                <div className="py-5 text-center text-muted">대리점 링크를 불러오는 중입니다.</div>
              ) : marketersQuery.isError || linksQuery.isError ? (
                <div className="py-5 text-center text-danger">대리점 링크를 불러오지 못했습니다.</div>
              ) : links.length === 0 ? (
                <div className="py-5 text-center text-muted">
                  <div className="mb-3">발급된 대리점 링크가 없습니다.</div>
                  <Button color="soft-primary" onClick={() => navigate("/member-invite")}>
                    <UserPlus size={16} className="me-1" />
                    마케터 멤버 추가
                  </Button>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table align-middle table-nowrap mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>링크명</th>
                        <th>마케터</th>
                        <th>수수료율</th>
                        <th>신청 링크</th>
                        <th>상태</th>
                        <th>최근 사용</th>
                        <th className="text-end">액션</th>
                      </tr>
                    </thead>
                    <tbody>
                      {links.map((link) => {
                        const marketer = marketerByUser.get(link.agency_user);
                        return (
                          <tr key={link.id}>
                            <td>
                              <div className="fw-semibold">{link.label}</div>
                              <div className="text-muted fs-12">{link.memo || "-"}</div>
                            </td>
                            <td>
                              <div>{marketerLabel(marketer)}</div>
                              <div className="text-muted fs-12">{marketer?.phone_num || "-"}</div>
                            </td>
                            <td>{link.commission_rate}%</td>
                            <td>
                              <div className="text-truncate" style={{ maxWidth: 280 }}>
                                {buildAgencyUrl(link.token)}
                              </div>
                            </td>
                            <td>
                              <Badge color={link.is_active ? "success" : "secondary"}>
                                {link.is_active ? "활성" : "비활성"}
                              </Badge>
                            </td>
                            <td>{formatDateTime(link.last_used_at)}</td>
                            <td className="text-end">
                              <Button size="sm" color="soft-secondary" className="me-2" onClick={() => copyUrl(link.token)}>
                                <Copy size={14} className="me-1" />
                                복사
                              </Button>
                              <Button size="sm" color="soft-primary" onClick={() => startEdit(link)}>
                                <Edit2 size={14} className="me-1" />
                                수정
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </GroupReservationPageShell>
  );
}
